import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    Clock,
    CalendarDays,
    CheckCircle,
    XCircle,
    Sunrise,
    Sunset,
    Zap,
    Users,
    Activity,
    AlertTriangle
} from 'lucide-react';
import {
    attendanceCheckIn,
    attendanceCheckOut,
    getAttendanceCalendar,
    getAttendanceMonthlySummary
} from "../services/employee.service";

console.log("🔥🔥 AttendancePage LOADED 🔥🔥");

// =====================================================================
// ATTENDANCE DASHBOARD COMPONENT
// =====================================================================

const AttendancePage = ({ onBack }) => {
    // --- State Management ---
    const [currentTime, setCurrentTime] = useState(new Date());
    const [punchIn, setPunchIn] = useState(null);
    const [punchOut, setPunchOut] = useState(null);
    const [workedHours, setWorkedHours] = useState("00:00:00");
    const [statusMessage, setStatusMessage] = useState("Ready to start your day.");
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    
    // Backend Data States
    const [backendCalendar, setBackendCalendar] = useState([]);
    const [summaryData, setSummaryData] = useState({
        total_days: 0,
        present: 0,
        absent: 0,
        leave: 0
    });
    
    // Hardcoded location for a more realistic feel
    const currentGeoLocation = "Bengaluru, India (Remote)";
    
    // --- Live Clock and Worked Hours Logic ---

    // 1. Live clock update every second
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Initial Data Fetch & State Restoration
    useEffect(() => {
        // --- ADDED DEBUGGING LOGS AT THE TOP ---
        console.log("🔥🔥 Attendance fetch useEffect RUNNING");

        const fetchData = async () => {
            try {
                console.log("🚀 fetchData STARTED");

                const now = new Date();
                const params = { 
                    year: now.getFullYear(), 
                    month: now.getMonth() + 1 
                };

                console.log("📦 Params:", params);

                const [calendarRes, summaryRes] = await Promise.all([
                    getAttendanceCalendar(params),
                    getAttendanceMonthlySummary(params)
                ]);

                console.log("✅ Calendar response:", calendarRes);
                console.log("✅ Summary response:", summaryRes);

                // --- Processing Logic ---
                
                // FIX 3: Safety fix for backend calendar response
                setBackendCalendar(Array.isArray(calendarRes) ? calendarRes : []);

                // FIX 1: Replace summary handling to map array response to object
                const empCode = localStorage.getItem("emp_code");
                const mySummaryRow = (summaryRes || []).find(
                    row => row[0] === empCode
                );

                if (mySummaryRow) {
                    const present = mySummaryRow[2] || 0;
                    const absent = mySummaryRow[3] || 0;
                    const leave = mySummaryRow[4] || 0;

                    setSummaryData({
                        total_days: present + absent + leave,
                        present,
                        absent,
                        leave
                    });
                } else {
                    setSummaryData({
                        total_days: 0,
                        present: 0,
                        absent: 0,
                        leave: 0
                    });
                }

                // RESTORE STATE: Find today's record in the calendar
                const todayStr = now.toISOString().split('T')[0];
                const todayRecord = (Array.isArray(calendarRes) ? calendarRes : []).find(
                    (item) => item.date === todayStr
                );

                if (todayRecord) {
                    if (todayRecord.in_time) {
                        setPunchIn(new Date(todayRecord.in_time));
                        setStatusMessage(`Punched In at ${new Date(todayRecord.in_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}.`);
                    }
                    if (todayRecord.out_time) {
                        setPunchOut(new Date(todayRecord.out_time));
                        setStatusMessage(`Punched Out! Total time today recorded.`);
                    }
                }
            } catch (err) {
                console.error("❌ Attendance fetch failed:", err);
                setApiError("Could not load attendance history.");
            }
        };

        fetchData();
    }, []);

    // 2. Calculate session time
    useEffect(() => {
        if (!punchIn || punchOut) return; 

        const interval = setInterval(() => {
            const diff = new Date() - punchIn;
            const hrs = Math.floor(diff / (1000 * 60 * 60));
            const mins = Math.floor((diff / (1000 * 60)) % 60);
            const secs = Math.floor((diff / 1000) % 60);

            setWorkedHours(
                `${hrs.toString().padStart(2, "0")}:${mins
                    .toString()
                    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [punchIn, punchOut]);

    // --- Action Handlers ---

    const handlePunchIn = async () => {
        try {
            setLoading(true);
            setApiError(null);
            await attendanceCheckIn();
            const now = new Date();
            setPunchIn(now);
            setPunchOut(null);
            setWorkedHours("00:00:00");
            setStatusMessage(`Punched In at ${now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}.`);
        } catch (err) {
            const errorMsg = punchIn && !punchOut ? "You are already checked in for today." : (err || "Check-in failed. Please try again.");
            setApiError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handlePunchOut = async () => {
        try {
            setLoading(true);
            setApiError(null);
            await attendanceCheckOut();
            const now = new Date();
            setPunchOut(now);
            setStatusMessage(`Punched Out! Total time today: ${workedHours}.`);
        } catch (err) {
            let errorMsg = "Check-out failed. Please try again.";
            if (!punchIn) errorMsg = "You have not checked in yet.";
            else if (punchOut) errorMsg = "You are already checked out for today.";
            else if (err) errorMsg = err;
            setApiError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Replace Mock Logic with Backend Data Mapping
    const calendarData = useMemo(() => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth();
        
        const firstDayOfMonth = new Date(year, month, 1).getDay(); 
        const totalDays = new Date(year, month + 1, 0).getDate();

        const days = [];

        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push({ id: `empty-${i}`, empty: true });
        }

        for (let d = 1; d <= totalDays; d++) {
            const dateObj = new Date(year, month, d);
            const dateISO = dateObj.toISOString().split('T')[0];
            const record = backendCalendar.find(r => r.date === dateISO);
            
            let dayStatus = "none"; 
            
            if (record) {
                // FIX 2: Update calendar status mapping for backend codes
                const map = {
                    'P': 'present',
                    'WFH': 'present',
                    'A': 'absent',
                    'L': 'leave',
                    'HALF_DAY': 'leave'
                };
                dayStatus = map[record.status] || "none";
            }

            if (dateObj.getDay() === 0 || dateObj.getDay() === 6) {
                dayStatus = "weekend";
            }
            if (d === today.getDate() && month === today.getMonth() && year === today.getFullYear()) {
                dayStatus = 'today';
            }

            days.push({ id: d, day: d, status: dayStatus });
        }

        return days;
    }, [backendCalendar, currentTime]);

    // --- Derived States ---
    const isPunchedIn = !!punchIn && !punchOut;
    const punchButtonText = loading ? "Processing..." : (isPunchedIn ? "Check Out" : "Check In");
    const punchButtonAction = isPunchedIn ? handlePunchOut : handlePunchIn;
    const punchButtonDisabled = punchOut || loading;
    const buttonStyle = punchButtonDisabled 
        ? "bg-gray-400 cursor-not-allowed" 
        : isPunchedIn 
            ? "bg-red-600 hover:bg-red-700 shadow-red-500/50" 
            : "bg-green-600 hover:bg-green-700 shadow-green-500/50";


    // --- Helper Components ---

    const StatusCard = ({ icon: Icon, title, value, color, iconBg, size = "md" }) => (
        <div className="p-4 bg-white rounded-xl shadow-md border border-gray-200 flex items-center space-x-3 transition-shadow hover:shadow-lg">
            <div className={`p-2 rounded-full ${iconBg} text-${color}-600`}>
                <Icon size={size === "lg" ? 28 : 20} />
            </div>
            <div>
                <p className="text-xs font-roboto font-medium text-gray-500 uppercase">{title}</p>
                <p className={`text-lg font-poppins font-bold text-gray-800 ${size === "lg" ? 'text-2xl' : ''}`}>{value}</p>
            </div>
        </div>
    );

    const CalendarDay = ({ day, status }) => {
        let classes = 'p-2 rounded-lg text-center font-roboto text-sm h-10 w-10 flex items-center justify-center font-semibold';
        let tooltip = '';

        switch (status) {
            case 'today':
                classes += ' bg-indigo-600 text-white shadow-lg ring-2 ring-indigo-300';
                tooltip = 'Today - Current Status';
                break;
            case 'present':
                classes += ' bg-green-100 text-green-700 hover:bg-green-200';
                tooltip = 'Present';
                break;
            case 'absent':
                classes += ' bg-red-100 text-red-700 hover:bg-red-200';
                tooltip = 'Absent / Holiday';
                break;
            case 'leave':
                classes += ' bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
                tooltip = 'On Leave';
                break;
            case 'weekend':
                classes += ' bg-gray-200 text-gray-500 italic';
                tooltip = 'Weekend Off';
                break;
            default:
                classes += ' text-gray-700';
        }

        return (
            <div title={tooltip} className={classes}>
                {day}
            </div>
        );
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen p-4 sm:p-6 lg:p-10 bg-gray-100"
            style={{ fontFamily: "Roboto, Poppins, sans-serif" }}
        >
            <header className="mb-6 lg:mb-10 flex justify-between items-center border-b pb-4 border-gray-200">
                <div className="flex items-center space-x-3">
                    <Clock size={32} className="text-indigo-600 min-w-8" />
                    <h1 className="text-3xl font-poppins font-extrabold text-gray-900">
                        Attendance Dashboard
                    </h1>
                </div>
                <button
                    onClick={onBack}
                    className="flex items-center text-sm font-roboto font-semibold text-indigo-600 hover:text-indigo-800 transition p-2 rounded-lg bg-indigo-50"
                >
                    <ArrowLeft size={18} className="mr-1" />
                    Back to Portal
                </button>
            </header>

            <div className="lg:grid lg:grid-cols-3 lg:gap-8">
                <div className="lg:col-span-1 space-y-6">
                    <div className="p-6 bg-white rounded-2xl shadow-xl border border-gray-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-poppins font-bold text-gray-800 flex items-center">
                                <Zap size={20} className="mr-2 text-yellow-500" />
                                Live Clock
                            </h2>
                            <span className="text-xs font-roboto text-gray-500">
                                {currentTime.toLocaleDateString()}
                            </span>
                        </div>
                        <p className="text-5xl sm:text-6xl font-poppins font-extrabold text-gray-900 tracking-tighter mt-3">
                            {currentTime.toLocaleTimeString()}
                        </p>
                        <p className="text-sm font-roboto text-gray-500 mt-2 flex items-center">
                            <Users size={14} className="mr-2" />
                            Location: {currentGeoLocation}
                        </p>
                    </div>

                    <div className="p-6 bg-white rounded-2xl shadow-xl border border-gray-200">
                        <h2 className="text-xl font-poppins font-bold text-gray-800 mb-4">
                            Today's Action
                        </h2>

                        <button
                            onClick={punchButtonAction}
                            disabled={punchButtonDisabled}
                            className={`w-full py-4 rounded-xl text-white text-lg font-poppins font-semibold transition-all duration-300 transform hover:scale-[1.01] shadow-lg ${buttonStyle}`}
                        >
                            {punchButtonText}
                        </button>
                        
                        <div className="mt-4 p-3 rounded-lg border border-indigo-200 bg-indigo-50 text-sm font-roboto text-indigo-800">
                            <p className='font-semibold'>Status: {statusMessage}</p>
                        </div>
                        
                        {apiError && (
                            <div className="mt-2 text-sm text-red-600 font-roboto">
                                {apiError}
                            </div>
                        )}
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-lg font-poppins font-semibold text-gray-700 mt-2">Time Details</h3>
                        <StatusCard 
                            icon={Activity}
                            title="Worked Hours"
                            value={workedHours}
                            color="indigo"
                            iconBg="bg-indigo-100"
                            size="lg"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <StatusCard 
                                icon={Sunrise}
                                title="Check In"
                                value={punchIn ? punchIn.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                color="amber"
                                iconBg="bg-amber-100"
                            />
                            <StatusCard 
                                icon={Sunset}
                                title="Check Out"
                                value={punchOut ? punchOut.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                color="red"
                                iconBg="bg-red-100"
                            />
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-2 mt-8 lg:mt-0 space-y-6">
                    <div className="p-6 bg-white rounded-2xl shadow-xl border border-gray-200">
                        <h2 className="text-xl font-poppins font-bold text-gray-800 mb-5 flex items-center">
                            <CalendarDays size={20} className="mr-2 text-sky-600" />
                            Monthly View - {currentTime.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                        </h2>
                        
                        <div className="grid grid-cols-7 gap-2">
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                                <div
                                    key={d}
                                    className="text-center text-xs font-poppins font-bold text-gray-500 p-2"
                                >
                                    {d}
                                </div>
                            ))}

                            {calendarData.map((item) =>
                                item.empty ? (
                                    <div key={item.id} className="h-10 w-10"></div>
                                ) : (
                                    <CalendarDay 
                                        key={item.id} 
                                        day={item.day} 
                                        status={item.status} 
                                    />
                                )
                            )}
                        </div>
                        
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm font-roboto text-gray-600 pt-4 border-t border-gray-100">
                            <LegendItem icon={CheckCircle} color="green" text="Present" />
                            <LegendItem icon={XCircle} color="red" text="Absent" />
                            <LegendItem icon={AlertTriangle} color="yellow" text="Leave" />
                            <LegendItem color="indigo" text="Today" type="color" />
                            <LegendItem color="gray" text="Weekend" type="color" />
                        </div>
                    </div>

                    <div className="p-6 bg-white rounded-2xl shadow-xl border border-gray-200">
                        <h2 className="text-xl font-poppins font-bold text-gray-800 mb-5 flex items-center">
                            <Activity size={20} className="mr-2 text-pink-600" />
                            Monthly Summary
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <MiniSummaryCard label="Total Days" value={summaryData.total_days || 0} color="indigo" />
                            <MiniSummaryCard label="Days Present" value={summaryData.present || 0} color="green" />
                            <MiniSummaryCard label="Days Absent" value={summaryData.absent || 0} color="red" />
                            <MiniSummaryCard label="Days on Leave" value={summaryData.leave || 0} color="yellow" />
                        </div>
                    </div>
                </div>
            </div>

            <footer className="mt-10 pt-4 border-t border-gray-300 text-center text-sm font-roboto text-gray-500">
                <p>Attendance tracked as per company policy. Geo-location logging active.</p>
            </footer>

        </motion.div>
    );
};

// --- Sub-Components ---

const MiniSummaryCard = ({ label, value, color }) => (
    <div className={`p-4 rounded-xl border border-${color}-200 bg-${color}-50`}>
        <p className="text-2xl font-poppins font-bold text-gray-900">{value}</p>
        <p className={`text-xs font-roboto text-${color}-600 uppercase mt-1`}>{label}</p>
    </div>
);

const LegendItem = ({ icon: Icon, color, text, type = 'icon' }) => (
    <div className="flex items-center space-x-1.5">
        {type === 'icon' ? (
            <Icon size={16} className={`text-${color}-600`} />
        ) : (
            <div className={`h-3 w-3 rounded-full bg-${color}-600`}></div>
        )}
        <span>{text}</span>
    </div>
);

export default AttendancePage;